import type { EventFormData } from "@/features/study-group-events/type";
import type { EventUpdateRequest } from "@/lib/humhub-api/events";
import type { EventCreateApiRequest } from "@/lib/humhub-api/types";
import { transformFormToUpdateApiRequest } from "@/lib/utils/eventUtils";

const isEqualValue = (a: unknown, b: unknown) => {
  if (
    Array.isArray(a) ||
    Array.isArray(b) ||
    typeof a === "object" ||
    typeof b === "object"
  ) {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return a === b;
};

const pickDiff = <T extends Record<string, unknown>>(
  prev: T,
  next: T,
): Partial<T> => {
  const diff: Partial<T> = {};
  Object.keys(next).forEach((key) => {
    const typedKey = key as keyof T;
    if (!isEqualValue(prev[typedKey], next[typedKey])) {
      diff[typedKey] = next[typedKey];
    }
  });
  return diff;
};

const pickTimeFields = (
  source?: EventCreateApiRequest["CalendarEntryForm"],
  options?: { includeTimeZone?: boolean },
): Partial<EventCreateApiRequest["CalendarEntryForm"]> | undefined => {
  if (!source) return undefined;
  const includeTimeZone = options?.includeTimeZone ?? true;
  const next: Partial<EventCreateApiRequest["CalendarEntryForm"]> = {
    start_date: source.start_date,
    end_date: source.end_date,
  };
  if (includeTimeZone) {
    next.timeZone = source.timeZone;
  }
  if ("start_time" in source && source.start_time) {
    next.start_time = source.start_time;
  }
  if ("end_time" in source && source.end_time) {
    next.end_time = source.end_time;
  }
  return next;
};

/**
 * 差分のみを Event update API 用のペイロードとして構築する。
 * 時刻・all_day が変わる場合は必須フィールド欠落を防ぐためフォーム全体を送る。
 */
export const buildEventUpdateDiff = (
  prevFormData: EventFormData,
  nextFormData: EventFormData,
  existingTimeZone: string,
): EventUpdateRequest => {
  const prevReq = transformFormToUpdateApiRequest(
    prevFormData,
    existingTimeZone,
  );
  const nextReq = transformFormToUpdateApiRequest(
    nextFormData,
    existingTimeZone,
  );

  const diff: EventUpdateRequest = {};
  const prevScope = prevReq.CalendarEntryForm?.scope;
  const nextScope = nextReq.CalendarEntryForm?.scope;

  const entryDiff = pickDiff(
    prevReq.CalendarEntry as unknown as Record<string, unknown>,
    nextReq.CalendarEntry as unknown as Record<string, unknown>,
  ) as unknown as EventCreateApiRequest["CalendarEntry"];
  if (entryDiff && Object.keys(entryDiff).length > 0) {
    diff.CalendarEntry = entryDiff;
  }

  const formDiff = pickDiff(
    prevReq.CalendarEntryForm as unknown as Record<string, unknown>,
    nextReq.CalendarEntryForm as unknown as Record<string, unknown>,
  ) as unknown as EventCreateApiRequest["CalendarEntryForm"];

  const timeKeys = [
    "start_date",
    "end_date",
    "start_time",
    "end_time",
    "timeZone",
  ] as const;
  const timeRelatedChanged =
    !isEqualValue(
      prevReq.CalendarEntry?.all_day,
      nextReq.CalendarEntry?.all_day,
    ) ||
    timeKeys.some(
      (key) =>
        !isEqualValue(
          prevReq.CalendarEntryForm?.[key],
          nextReq.CalendarEntryForm?.[key],
        ),
    );
  const prevAllDay = prevReq.CalendarEntry?.all_day;
  const nextAllDay = nextReq.CalendarEntry?.all_day;
  const allDayOffToOn = prevAllDay === 0 && nextAllDay === 1;
  const timeZoneChanged = !isEqualValue(
    prevReq.CalendarEntryForm?.timeZone,
    nextReq.CalendarEntryForm?.timeZone,
  );
  const hasRecurringContext = Boolean(
    prevFormData.recurrence || nextFormData.recurrence,
  );
  const allDayChanged = prevAllDay !== nextAllDay;
  const shouldSendSafeSetForTimeZoneChange = timeZoneChanged && !allDayOffToOn;
  const shouldIncludeTimeZoneInTimeFields = !allDayOffToOn;

  let nextCalendarEntryForm:
    | Partial<EventCreateApiRequest["CalendarEntryForm"]>
    | undefined;

  // all_day/時刻系が変わる場合は時間系のみ送信。それ以外は通常差分。
  if (timeRelatedChanged) {
    nextCalendarEntryForm = pickTimeFields(nextReq.CalendarEntryForm, {
      includeTimeZone: shouldIncludeTimeZoneInTimeFields,
    });
  } else if (formDiff && Object.keys(formDiff).length > 0) {
    nextCalendarEntryForm = formDiff;
  }

  // タイムゾーン変更時は安全セットで時間系+all_dayを必ず送信
  if (shouldSendSafeSetForTimeZoneChange) {
    nextCalendarEntryForm = pickTimeFields(nextReq.CalendarEntryForm, {
      includeTimeZone: true,
    });
    if (nextAllDay !== undefined) {
      diff.CalendarEntry = {
        ...(diff.CalendarEntry ?? {}),
        all_day: nextAllDay,
      } as EventCreateApiRequest["CalendarEntry"];
    }
    // 繰り返し編集時は scope を明示的に送る
    if (hasRecurringContext) {
      const scopeToSend =
        nextReq.CalendarEntryForm?.scope ?? nextFormData.scope;
      if (scopeToSend) {
        diff.scope = scopeToSend;
      }
    }
  }

  // all_day 変更時は CalendarEntry の all_day を必ず含める
  if (allDayChanged) {
    if (nextAllDay !== undefined) {
      diff.CalendarEntry = {
        ...(diff.CalendarEntry ?? {}),
        all_day: nextAllDay,
      } as EventCreateApiRequest["CalendarEntry"];
    }
    nextCalendarEntryForm = pickTimeFields(nextReq.CalendarEntryForm, {
      includeTimeZone: shouldIncludeTimeZoneInTimeFields,
    });
  }

  // 終日へ切替（off->on）のときは timeZone を送らない
  if (nextCalendarEntryForm) {
    if (allDayOffToOn) {
      const { timeZone: _timeZone, ...restForm } = nextCalendarEntryForm;
      nextCalendarEntryForm =
        restForm as EventCreateApiRequest["CalendarEntryForm"];
    }
    diff.CalendarEntryForm =
      nextCalendarEntryForm as EventCreateApiRequest["CalendarEntryForm"];
  }

  if (!isEqualValue(prevScope, nextScope) && nextScope !== undefined) {
    diff.scope = nextScope;
  }

  if (diff.CalendarEntryForm) {
    const { scope: _formScope, ...restForm } = diff.CalendarEntryForm;
    diff.CalendarEntryForm =
      restForm as EventCreateApiRequest["CalendarEntryForm"];
    if (Object.keys(diff.CalendarEntryForm).length === 0) {
      delete diff.CalendarEntryForm;
    }
  }

  // scope 単体の差分は「更新」ではなく適用範囲指定なので除外する
  const { scope: _scopeOnly, ...restDiff } = diff;
  const hasOtherDiff = Object.keys(restDiff).length > 0;
  if (!hasOtherDiff) {
    delete diff.scope;
  }

  return diff;
};
